import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Embeds = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Event Embeds let you display your Event on external websites using
            an embeddable widget. This is useful for promoting Events on your
            organization&apos;s website, blog, or community forum without
            requiring visitors to leave the page.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-embeds.png'
          alt={t`Embeds settings page showing embed preview and HTML code snippet — Event Embeds follow the same pattern`}
          caption={t`Event Embeds use the same embed code pattern as Guild Embeds`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='embed-usage' title={t`Using Embeds`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='window-new'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Getting the embed code
                  </Paragraph>{' '}
                  — Open{' '}
                  <Link href='/docs/events/settings'>Event Settings</Link> and
                  go to the Embeds section. Copy either the iframe snippet or
                  the SDK snippet, which you can paste into an external website.
                  The widget loads with isolated styling, so it will not
                  conflict with your site&apos;s CSS.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    What the embed shows
                  </Paragraph>{' '}
                  — The embedded widget displays the Event&apos;s cover photo,
                  title, date and time, host Guild, and an action button for
                  RSVPing or purchasing tickets. It provides a compact summary
                  that encourages visitors to engage with the Event directly
                  from your site.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='link'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Keeping content in sync
                  </Paragraph>{' '}
                  — The embed always reflects the latest Event information. If
                  you update the Event title, date, cover photo, or any other
                  detail in{' '}
                  <Link href='/docs/events/settings'>Event Settings</Link>, the
                  embedded widget updates automatically — there is no need to
                  regenerate or re-paste the embed code.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='guild-embeds' title={t`Guild Embeds`}>
          <Paragraph size='$5'>
            <Trans>
              For embedding your Guild&apos;s full profile or Events listing,
              see <Link href='/docs/guilds/embeds'>Guild Embeds</Link>.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Embeds
